// src/pages/Login.js
import React, { useState } from 'react';
import '../css/Login.css';
import axios from 'axios';
import { forgot_password_send_mail, login_web_request } from '../../utils/authRequests';
import { useAuth } from '../../navigation/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { CompanyPickerPopup } from '../../components/CompanyPickerPopup';
import { BACKEND_URL } from '../../constants';

const EyeIcon = ({ visible }) =>
  visible ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5.05 0-9.27-3.14-11-7.5a11.09 11.09 0 0 1 4.27-5.09M1 1l22 22" />
      <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 2.47-6M12 12" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

const ButtonSpinner = () => (
  <svg width="16" height="16" viewBox="0 0 50 50" style={{ marginRight: 8, verticalAlign: 'middle' }} aria-hidden="true">
    <circle cx="25" cy="25" r="20" strokeWidth="5" stroke="currentColor" fill="none" opacity="0.2" />
    <circle cx="25" cy="25" r="20" strokeWidth="5" stroke="currentColor" fill="none" strokeDasharray="90,150">
      <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.75s" repeatCount="indefinite" />
    </circle>
  </svg>
);

export const Login = () => {
  const {
    setUser, setCompany, setEmployees, setProjects, setCustomers,
    setDraftSchedule, setUsersHours, setTimeSchemas, setMostRecentTimeSchema,
    setPays, setObs, setOvertimeRules, setVacationPay, setAbsenceRules,
    setSalaryProfiles, setObUpdateInfo, setProjectGroups, setReports, setReportCategories
  } = useAuth();

  const navigate = useNavigate();

  // form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // reset password modal state
  const [resetPassEmail, setResetPassEmail] = useState("");
  const [resetPassPopupVisible, setResetPassPopupVisible] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // small UI bits
  const [isLoginBtnPressed, setIsLoginBtnPressed] = useState(false);

  // local loading states
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSelectingCompany, setIsSelectingCompany] = useState(false);

  // company selection
  const [companyOptions, setCompanyOptions] = useState(null); // null | [{ _id, name }]
  const [pendingUserId, setPendingUserId] = useState(null);   // keep userId for dev-mode /select-company

  const applyBootstrap = (data) => {
    console.log("apply bootstrap data --> ", data)
    setUser(data.user);
    setCompany(data.company);
    setEmployees(data.employees);
    setProjects(data.projects);
    setCustomers(data.customers);
    setDraftSchedule(data.shifts);
    if (data.usersHours) setUsersHours(data.usersHours);
    setTimeSchemas(data.timeSchemas);
    setMostRecentTimeSchema(data.mostRecentTimeSchema);
    setPays(data.pays);
    setObs(data.obs);
    setOvertimeRules(data.overtimeRules);
    setVacationPay(data.vacationPay);
    setSalaryProfiles(data.salaryProfiles);
    setAbsenceRules(data.absenceRules);
    setProjectGroups(data.projectGroups);
    setReports(data.reports);
    setReportCategories(data.reportCategories);
    if (data.company?.isOBUpdateActive) {
      setObUpdateInfo({
        isUpdating: true,
        status: "preparing",
        progress: 0,
        total: 0,
        companyId: data.company._id,
        startedAt: Date.now(),
        mode: "resumed",
      });
    }
  };

  // /select-company – always send userId if we have it (ignored in prod, used in dev)
  const selectCompanyRequest = async ({ companyId, userId }) => {
    const { data } = await axios.post(
      `${BACKEND_URL}/select-company`,
      { companyId, userId },
      { withCredentials: true }
    );
    return data;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (isLoggingIn) return;
    const lowerCaseEmail = email.toLowerCase();

    try {
      setIsLoggingIn(true);
      const data = await login_web_request({ email: lowerCaseEmail, password });

      if (data.status === "failure") {
        alert(data.errorText);
        return;
      }

      if (data.status === "choose-company") {
        const companies = Array.isArray(data.companies) ? data.companies : [];
        setPendingUserId(data.user?._id || null);
        setCompanyOptions(companies);
        return;
      }

      if (data.status === "success") {
        applyBootstrap(data);
        navigate("/shifts");
      }
    } catch (error) {
      console.error("Login error:", error);
      alert("Ett fel uppstod. Försök igen.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleChooseCompany = async (companyId) => {
    if (isSelectingCompany) return;
    try {
      setIsSelectingCompany(true);
      const data = await selectCompanyRequest({ companyId, userId: pendingUserId });

      if (data.status === "failure") {
        alert(data.errorText || "Kunde inte välja företag.");
        return;
      }
      if (data.status === "success") {
        setCompanyOptions(null);
        applyBootstrap(data);
        navigate("/shifts");
      }
    } catch (error) {
      console.error("Select company error:", error);
      alert("Kunde inte välja företag. Försök igen.");
    } finally {
      setIsSelectingCompany(false);
    }
  };

  const handleSubmitResetPassword = async () => {
    if (isResettingPassword) return;
    if (!resetPassEmail) return alert("Du måste ange en giltigt email adress!");
    
    try {
      setIsResettingPassword(true);
      const data = await forgot_password_send_mail({ email: resetPassEmail });
      
      if (data.status === "success") {
        alert("Du har fått en länk skickad till din mail");
        setResetPassPopupVisible(false);
        setResetPassEmail("");
      } else {
        alert(data.errorText);
      }
    } catch (error) {
      console.error("Reset password error:", error);
      alert("Ett fel uppstod vid återställning av lösenord.");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCloseResetPasswordPopup = () => {
    if (isResettingPassword) return;
    setResetPassPopupVisible(false);
    setResetPassEmail("");
  };

  const renderResetPasswordPopup = () => {
    const stylesPopup = {
      resetPasswordPopup: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(103, 143, 180, 0.77)',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
        width: '400px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(20px)',
        zIndex: 1200000,
      },
      heading: { fontSize: '24px', color: '#fcfafa', marginBottom: '20px' },
      label: { fontSize: '14px', color: '#fcfafa', marginBottom: '8px', alignSelf: 'flex-start' },
      input: {
        width: '100%',
        padding: '10px',
        marginBottom: '20px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '14px',
        boxSizing: 'border-box',
        opacity: isResettingPassword ? 0.7 : 1,
      },
      buttonContainer: { display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' },
      button: {
        backgroundColor: '#5DADE2',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '10px 15px',
        fontSize: '14px',
        cursor: isResettingPassword ? 'not-allowed' : 'pointer',
        width: '100px',
        opacity: isResettingPassword ? 0.7 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
      },
      cancelButton: { backgroundColor: '#6c757d' },
      buttonHover: { backgroundColor: '#62b7f0' },
      cancelButtonHover: { backgroundColor: '#76818a' },
    };

    return (
      <div style={stylesPopup.resetPasswordPopup}>
        <h1 style={stylesPopup.heading}>Nollställ Lösenord</h1>
        <label style={stylesPopup.label}>Email:</label>
        <input
          style={stylesPopup.input}
          placeholder="email-adress"
          value={resetPassEmail}
          onChange={(e) => setResetPassEmail(e.target.value)}
          disabled={isResettingPassword}
        />
        <div style={stylesPopup.buttonContainer}>
          <button
            style={stylesPopup.button}
            onMouseOver={(e) => !isResettingPassword && (e.target.style.backgroundColor = stylesPopup.buttonHover.backgroundColor)}
            onMouseOut={(e) => !isResettingPassword && (e.target.style.backgroundColor = stylesPopup.button.backgroundColor)}
            onClick={handleSubmitResetPassword}
            disabled={isResettingPassword}
          >
            {isResettingPassword ? <><ButtonSpinner /> Skickar...</> : 'Ok'}
          </button>
          <button
            style={{ ...stylesPopup.button, ...stylesPopup.cancelButton }}
            onMouseOver={(e) => !isResettingPassword && (e.target.style.backgroundColor = stylesPopup.cancelButtonHover.backgroundColor)}
            onMouseOut={(e) => !isResettingPassword && (e.target.style.backgroundColor = stylesPopup.cancelButton.backgroundColor)}
            onClick={handleCloseResetPasswordPopup}
            disabled={isResettingPassword}
          >
            Avbryt
          </button>
        </div>
      </div>
    );
  };

  const inputsDisabled = isLoggingIn || isSelectingCompany;

  return (
    <section className="page-section" style={styles.pageSection}>
      {[...Array(5)].map((_, index) => (
        <div key={index} className={`bubble bubble-${index + 1}`}></div>
      ))}

      <div className="login-page" style={styles.loginPage}>
        <div className="login-content">
          <div className="login-form-container" style={styles.loginFormContainer}>
            <form className="login-form" style={styles.loginForm} onSubmit={handleLogin}>
              <div className="imgcontainer" style={styles.imgContainer}>
                <h1 className="brand-title" style={styles.brandTitle}>𝙏𝙞𝙢𝐞𝙎𝙢𝙖𝙧t</h1>
              </div>

              <div className="input-control" style={styles.inputControl}>
                <input
                  className="input"
                  style={styles.input}
                  type="text"
                  placeholder="Ange email-adress"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={inputsDisabled}
                />

                <span className="password-field-show" style={styles.passwordFieldShow}>
                  <input
                    className="input"
                    style={styles.input}
                    type={showPassword ? "text" : "password"}
                    placeholder="Ange lösenord"
                    name="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={inputsDisabled}
                  />
                  <span
                    onClick={() => !inputsDisabled && setShowPassword(!showPassword)}
                    style={{ ...styles.eyeIcon, opacity: inputsDisabled ? 0.5 : 1, pointerEvents: inputsDisabled ? 'none' : 'auto' }}
                    title={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                  >
                    <EyeIcon visible={showPassword} />
                  </span>
                </span>

                <label
                  className="remember-me-label"
                  style={{ ...styles.rememberMeLabel, opacity: inputsDisabled ? 0.6 : 1, pointerEvents: inputsDisabled ? 'none' : 'auto' }}
                  onClick={() => setResetPassPopupVisible(true)}
                >
                  Glömt lösenord
                </label>

                <div className="login-btns" style={styles.loginBtns}>
                  <button
                    className="login-button"
                    type="submit"
                    disabled={isLoggingIn}
                    aria-busy={isLoggingIn}
                    onMouseDown={() => setIsLoginBtnPressed(true)}
                    onMouseUp={() => setIsLoginBtnPressed(false)}
                    onMouseLeave={() => setIsLoginBtnPressed(false)}
                    style={{
                      ...styles.loginButton,
                      transform: isLoginBtnPressed ? 'scale(0.98)' : 'scale(1)',
                      opacity: isLoggingIn ? 0.8 : 1,
                      cursor: isLoggingIn ? 'wait' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    {isLoggingIn ? (<><ButtonSpinner /> Loggar in…</>) : 'Logga in'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {resetPassPopupVisible && renderResetPasswordPopup()}

      {Array.isArray(companyOptions) && companyOptions.length > 0 && (
        <CompanyPickerPopup
          companies={companyOptions}
          onSelect={handleChooseCompany}
          onClose={() => setCompanyOptions(null)}
          isBusy={isSelectingCompany}
        />
      )}
    </section>
  );
};

const styles = {
  pageSection: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #5DADE2, #1f4e8c)',
  },
  loginPage: {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '15px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    maxWidth: '400px',
    width: '100%',
  },
  loginFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  brandTitle: {
    fontSize: '3em',
    textAlign: 'center',
    color: '#ffffff',
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    padding: '15px',
    margin: '10px 0',
    border: 'none',
    borderRadius: '5px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
  },
  passwordFieldShow: {
    position: 'relative',
    width: '100%',
  },
  eyeIcon: {
    position: 'absolute',
    right: '15px',
    top: '70%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
  },
  rememberMeLabel: {
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    marginTop: '10px',
    cursor: 'pointer',
  },
  loginBtns: {
    width: '100%',
    marginTop: '20px',
  },
  loginButton: {
    backgroundColor: '#5DADE2',
    color: '#ffffff',
    border: 'none',
    padding: '15px',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'transform 0.1s ease-in-out',
    width: '100%',
    transform: 'scale(1)',
  },
  imgContainer: {
    marginBottom: '20px',
  },
};

export default Login;